import React from 'react';
import { Box, Stack, TextField, Typography, alpha, useTheme } from '@mui/material';
import { CompareArrows as CompareIcon } from '@mui/icons-material';

const AnalogyStep = ({ config, response = {}, onChange, disabled }) => {
  const theme = useTheme();

  return (
    <Stack spacing={2.5}>
      {/* Banner */}
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        px: 1.5, py: 1.25,
        bgcolor: alpha(theme.palette.warning.main, 0.07),
        borderLeft: `3px solid ${theme.palette.warning.main}`,
        borderRadius: '0 6px 6px 0',
      }}>
        <CompareIcon sx={{ fontSize: 18, color: 'warning.main', mt: 0.15, flexShrink: 0 }} />
        <Box>
          <Typography variant="body2" fontWeight={700} color="warning.main">
            Find a real-life parallel
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
            Think of an everyday situation that works the same way — sorting files, filtering a shopping list, grouping
            receipts. The best analogies are ones you could explain to a friend with no tech background.
          </Typography>
        </Box>
      </Box>

      {/* The comparison */}
      <Box>
        <Typography variant="body2" fontWeight={700} mb={0.5}>
          This concept is like…
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
          Name the real-life thing — e.g. "filtering items in a shopping cart", "grouping receipts by category", "sorting a pile of letters by date"
        </Typography>
        <TextField
          value={response.analogy || ''}
          onChange={(e) => onChange({ ...response, analogy: e.target.value })}
          placeholder="e.g. Grouping receipts into labelled envelopes, one per shop…"
          fullWidth
          disabled={disabled}
          size="small"
        />
      </Box>

      {/* Why it's the same */}
      <Box>
        <Typography variant="body2" fontWeight={700} mb={0.5}>
          …because both work the same way:
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
          Explain the structural match. What does each real-life step correspond to in code?
        </Typography>
        <TextField
          value={response.because || ''}
          onChange={(e) => onChange({ ...response, because: e.target.value })}
          placeholder={
            'e.g. Each envelope = one GROUP. Totalling the receipts inside = SUM(). Checking the total before keeping the envelope = HAVING.\n' +
            'WHERE is like deciding which receipts to even put in the pile — before you start sorting.'
          }
          multiline
          minRows={4}
          fullWidth
          disabled={disabled}
          size="small"
        />
      </Box>

      {/* Where it breaks */}
      <Box>
        <Typography variant="body2" fontWeight={700} mb={0.5}>
          Where does the comparison break down?
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.75}>
          What can the code do that your real-life example cannot? (e.g. scale to millions of rows instantly)
        </Typography>
        <TextField
          value={response.limit || ''}
          onChange={(e) => onChange({ ...response, limit: e.target.value })}
          placeholder="e.g. You can't physically sort 10 million receipts, but SQL does it in milliseconds…"
          multiline
          minRows={2}
          fullWidth
          disabled={disabled}
          size="small"
        />
      </Box>
    </Stack>
  );
};

export default AnalogyStep;
